import { createTheme, DialogContent, Stack, ThemeProvider, useTheme } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { HoldToConfirmButton } from "@src/appV2/redesign/components/HoldToConfirmButton";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import {
  Illustration,
  PageHeaderWrapper,
  Text,
  Title,
} from "@src/appV2/redesign/system/ui-components";
import { useMemo } from "react";

export interface SkipBadgeVerificationContentProps {
  isSubmitting: boolean;
  onBack: () => void;
  onConfirmSkip: () => void;
  onGetTemporaryBadge: () => void;
}

export function SkipBadgeVerificationContent(props: SkipBadgeVerificationContentProps) {
  const { isSubmitting, onBack, onConfirmSkip, onGetTemporaryBadge } = props;

  const baseTheme = useTheme();
  const destructiveHoldButtonTheme = useMemo(
    () =>
      createTheme(baseTheme, {
        palette: {
          components: {
            holdToConfirmButton: {
              default: baseTheme.palette.background.tertiary,
              confirmed: baseTheme.palette.intent?.error?.background,
            },
          },
        },
      }),
    [baseTheme]
  );

  return (
    <Stack
      sx={(theme) => ({
        height: "100%",
        background: theme.palette.shift?.badgeSkippedWarning.gradient,
      })}
    >
      <PageHeaderWrapper variant="tertiary" sx={{ background: "transparent" }}>
        <PageHeaderTopRow onClickBack={onBack} />
      </PageHeaderWrapper>

      <DialogContent sx={{ paddingTop: 5 }}>
        <Stack spacing={10} alignItems="center" sx={{ textAlign: "center" }}>
          <Illustration type="warning-solid-error" sx={{ opacity: 0.5 }} />

          <Stack spacing={7} alignItems="center">
            <Title
              component="h1"
              variant="h2"
              sx={(theme) => ({ color: theme.palette.error.light })}
            >
              Skip badge verification?
            </Title>
            <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
              Alternatively, ask workplace staff if they can provide a temporary badge for your
              shift.
            </Text>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <ThemeProvider theme={destructiveHoldButtonTheme}>
          <HoldToConfirmButton
            fullWidth
            size="large"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            sx={(theme) => ({
              color: theme.palette.error.light,
              border: `${theme.borderWidth?.regular} solid ${theme.palette.border?.primary}`,
            })}
            onConfirm={onConfirmSkip}
          >
            <Text color={(theme) => theme.palette.intent?.error.text}>
              {isSubmitting ? "Skipping..." : "Press and hold to skip verification"}
            </Text>
          </HoldToConfirmButton>
        </ThemeProvider>
        <Button
          fullWidth
          variant="outlined"
          size="large"
          disabled={isSubmitting}
          onClick={onGetTemporaryBadge}
        >
          Get temporary badge
        </Button>
      </DialogFooter>
    </Stack>
  );
}
